import axios from 'axios'

const util = require('../../util.js')

const default_filter = {
  organizationNumber: null,
  ean: null,
  productName: null,
}

const default_paging = {
  currentPage: 1,
  perPage: 25,
  perPageAlternatives: [25, 50],
  sortBy: 'productName',
  sortDesc: false,
  totalRows: 0
}

const state = {
  licenses: [],
  usageCounts: {},
  totalUsageCounts: {},
  filter: { ...default_filter },
  paging: { ...default_paging },
  loading: false,
}

const mutations = {
  UPDATE_LICENSES (state, payload) {
    state.licenses = payload.response
    state.paging.totalRows = payload.resultCount
  },
  UPDATE_FILTER (state, payload) {
    state.filter = payload
  },
  UPDATE_ORGANIZATION_NUMBER (state, payload) {
    state.filter.organizationNumber = payload
  },
  UPDATE_PAGING (state, payload) {
    state.paging = payload
  },
  UPDATE_USAGE_COUNT (state, { licenseId, count }) {
    state.usageCounts = { ...state.usageCounts, [licenseId]: count }
  },
  RESET_USAGE_COUNTS (state) {
    state.usageCounts = {}
  },
  UPDATE_TOTAL_USAGE_COUNT (state, { licenseId, count }) {
    state.totalUsageCounts = { ...state.totalUsageCounts, [licenseId]: count }
  },
  RESET_TOTAL_USAGE_COUNTS (state) {
    state.totalUsageCounts = {}
  },
  UPDATE_LOADING (state, payload) {
    state.loading = payload
  },
}

const actions = {
  async getLicenses ({ commit, dispatch }) {
    commit('UPDATE_LOADING', true)
    await dispatch('getSelectedActiveProfile')
    commit('RESET_USAGE_COUNTS')
    commit('RESET_TOTAL_USAGE_COUNTS')

    try {
      const response = await axios.get(process.env.VUE_APP_INTERNAL_API_BASEURL + '/license/', {
        params: {
          licenseModel: util.addQueryParam(['INDIVIDUAL', 'ORGANIZATIONAL'], 'licenseModel'),
          organizationNumber: util.emptyToNull(state.filter.organizationNumber),
          ean: util.emptyToNull(state.filter.ean),
          productName: util.emptyToNull(state.filter.productName),
          isValid: true,
          pageNumber: util.emptyToNull(state.paging.currentPage),
          pageSize: util.emptyToNull(state.paging.perPage),
          orderBy: state.paging.sortBy + ':' + (state.paging.sortDesc ? 'desc' : 'asc'),
        }
      })

      const licenses = response.data.response.filter(l => l.usageLimited)

      if (licenses.length > 0) {
        const orgNumbers = new Set(
          licenses
            .filter(l => l.ownership === 'ORGANIZATION')
            .map(l => l.ownerOrganizationNumber)
        )

        if (orgNumbers.size > 0) {
          try {
            const orgs = await dispatch('organizations/getOrganizationByOrganizationNumbers', { organizationNumbers: orgNumbers }, { root: true })
            if (orgs && orgs.response) {
              licenses.forEach(license => {
                const org = orgs.response.find(o => o.organizationNumber === license.ownerOrganizationNumber)
                if (org) license.organizationName = org.name
              })
            }
          } catch {
            // Organization names are an enrichment only - list falls back to organizationNumber display
          }
        }
      }

      commit('UPDATE_LICENSES', { response: licenses, resultCount: response.data.resultCount })

      licenses.forEach(license => {
        dispatch('getUsageCount', license.licenseId)
        dispatch('getTotalUsageCount', license.licenseId)
      })
    } finally {
      commit('UPDATE_LOADING', false)
    }
  },

  async getUsageCount ({ commit }, licenseId) {
    try {
      const response = await axios.get(process.env.VUE_APP_INTERNAL_API_BASEURL + '/license/usages/', {
        params: { licenseId, status: 'ACTIVE', pageSize: 1 }
      })
      commit('UPDATE_USAGE_COUNT', { licenseId, count: response.data.resultCount })
    } catch (e) {
      commit('UPDATE_USAGE_COUNT', { licenseId, count: null })
    }
  },

  async getTotalUsageCount ({ commit }, licenseId) {
    try {
      const response = await axios.get(process.env.VUE_APP_INTERNAL_API_BASEURL + '/license/usages/', {
        params: { licenseId, pageSize: 1 }
      })
      commit('UPDATE_TOTAL_USAGE_COUNT', { licenseId, count: response.data.resultCount })
    } catch (e) {
      commit('UPDATE_TOTAL_USAGE_COUNT', { licenseId, count: null })
    }
  },

  resetFilter ({ commit }) {
    commit('UPDATE_PAGING', { ...default_paging })
    commit('UPDATE_FILTER', { ...default_filter })
    state.licenses = []
  },

  updateOrganization ({ commit }, organizationNumber) {
    commit('UPDATE_ORGANIZATION_NUMBER', organizationNumber)
  },

  getSelectedActiveProfile ({ commit, rootGetters }) {
    let isActiveProfileSelected = rootGetters['sessionStorage/isActiveProfileSet']
    if (isActiveProfileSelected) {
      let activeProfile = rootGetters['sessionStorage/activeProfile']
      if (activeProfile != null) {
        commit('UPDATE_ORGANIZATION_NUMBER', activeProfile.id)
      }
    }
  },

  updatePaging ({ commit, dispatch }, paging) {
    paging.currentPage = state.paging.currentPage
    paging.perPage = state.paging.perPage
    paging.totalRows = state.paging.totalRows
    paging.perPageAlternatives = default_paging.perPageAlternatives
    commit('UPDATE_PAGING', paging)
    state.licenses = []
    dispatch('getLicenses')
  },
}

const getters = {
  filter: state => state.filter,
  licenses: state => state.licenses,
  usageCounts: state => state.usageCounts,
  totalUsageCounts: state => state.totalUsageCounts,
  paging: state => state.paging,
  loading: state => state.loading,
}

const usageModule = {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}

export default usageModule
